/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AddressFieldDisplayCollectionModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface AddressInfoDisplayStyles {
    wrapper?: InjectableCss;
    customerNameText?: TypographyProps;
    firstNameText?: TypographyProps;
    lastNameText?: TypographyProps;
    attentionText?: TypographyProps;
    companyNameText?: TypographyProps;
    address1Text?: TypographyProps;
    address2Text?: TypographyProps;
    address3Text?: TypographyProps;
    address4Text?: TypographyProps;
    cityStatePostalCodeText?: TypographyProps;
    cityText?: TypographyProps;
    stateText?: TypographyProps;
    postalCodeText?: TypographyProps;
    countryText?: TypographyProps;
    phoneText?: TypographyProps;
    emailText?: TypographyProps;
    faxText?: TypographyProps;
}

export const addressInfoDisplayStyles: AddressInfoDisplayStyles = {
    wrapper: {
        css: css`
            font-style: normal;
        `,
    },
    customerNameText: { as: "p", weight: "bold", size: "15px", lineHeight: "19px" },
    attentionText: { as: "p", weight: "bold", size: "15px", lineHeight: "19px" },
    companyNameText: { as: "p", weight: "bold", size: "15px", lineHeight: "19px" },
    address1Text: { as: "p", size: "15px", lineHeight: "19px" },
    address2Text: { as: "p", size: "15px", lineHeight: "19px" },
    address3Text: { as: "p", size: "15px", lineHeight: "19px" },
    address4Text: { as: "p", size: "15px", lineHeight: "19px" },
    cityStatePostalCodeText: { as: "p", size: "15px", lineHeight: "19px" },
    countryText: { as: "p", size: "15px", lineHeight: "19px" },
    phoneText: { as: "p", size: "15px", lineHeight: "19px" },
    emailText: { as: "p", size: "15px", lineHeight: "19px" },
    faxText: { as: "p", size: "15px", lineHeight: "19px" },
};

interface OwnProps {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    attention?: string;
    address1: string;
    address2?: string;
    address3?: string;
    address4?: string;
    city: string;
    state?: string;
    postalCode: string;
    country?: string;
    phone?: string;
    fax?: string;
    email?: string;
    extendedStyles?: AddressInfoDisplayStyles;
    showAttnLabel?: boolean;
    properties?: any;
    customNumber?: string;

    // customization
    addressFields?: AddressFieldDisplayCollectionModel;
}

type Props = OwnProps;

const StyledAddress = getStyledWrapper("address");

const AddressInfoDisplay: React.FunctionComponent<Props> = function Address(props: Props) {
    const [styles] = React.useState(() => mergeToNew(addressInfoDisplayStyles, props.extendedStyles));
    const AF = props.addressFields;

    return (
        <StyledAddress {...styles.wrapper}  data-test-selector="addressInfoDisplay">
            <FullName 
                firstName={props.firstName} 
                lastName={props.lastName} 
                styles={styles}
                data-test-selector="addressInfoDisplay_fulName"
            />
            {props.companyName && (AF?.companyName?.isVisible || !AF) && (
                <Typography {...styles.companyNameText} data-test-selector="addressInfoDisplay_companyName">
                    {props.companyName}
                </Typography>
            )}
            {props.customNumber && (
                <Typography {...styles.companyNameText} data-test-selector="addressInfoDisplay_customNumber">
                    {props.customNumber}
                </Typography>
            )}
            {props.attention && (AF?.attention?.isVisible || !AF) && (
                <Typography {...styles.attentionText} data-test-selector="addressInfoDisplay_attention">
                    {props.showAttnLabel && translate("Attn: ")}
                    {props.attention}
                </Typography>
            )}
            {props.address1 && (AF?.companyName?.isVisible || !AF) && (
                <Typography {...styles.address1Text} data-test-selector="addressInfoDisplay_address1">
                    {props.address1}
                </Typography>
            )}
            {props.address2 && (AF?.address2?.isVisible || !AF) && (
                <Typography {...styles.address2Text} data-test-selector="addressInfoDisplay_address2">
                    {props.address2}
                </Typography>
            )}
            {(props?.address3 || props?.properties?.address3) && (AF?.address3?.isVisible || !AF) && (
                <Typography {...styles.address3Text} data-test-selector="addressInfoDisplay_address3">
                    {props.address3 || props.properties.address3}
                </Typography>
            )}
            {props?.address4 && (AF?.address4?.isVisible || !AF) && (
                <Typography {...styles.address4Text} data-test-selector="addressInfoDisplay_address4">
                    {props.address4}
                </Typography>
            )}

            <CityStatePostalCode
                city={props.city}
                state={props.state}
                postalCode={props.postalCode}
                styles={styles}
                addressFields={AF}
            />

            {props.country && (AF?.country?.isVisible || !AF) && (
                <Typography {...styles.countryText} data-test-selector="addressInfoDisplay_country">
                    {props.country}
                </Typography>
            )}
            {props.phone && (AF?.phone?.isVisible || !AF) && (
                <Typography {...styles.phoneText} data-test-selector="addressInfoDisplay_phoneNumber">
                    {props.phone}
                </Typography>
            )}
            {props.fax && (AF?.fax?.isVisible || !AF) && (
                <Typography {...styles.faxText} data-test-selector="addressInfoDisplay_fax">
                    {props.fax}
                </Typography>
            )}
            {props.email && (AF?.email?.isVisible || !AF) && (
                <Typography {...styles.emailText} data-test-selector="addressInfoDisplay_emailAddress">
                    {props.email}
                </Typography>
            )}
        </StyledAddress>
    );
};

interface FullNameProps {
    firstName?: string;
    lastName?: string;
    styles: AddressInfoDisplayStyles;
    addressFields?: AddressFieldDisplayCollectionModel;
}

const FullName = ({ firstName, lastName, styles, addressFields }: FullNameProps) => {
    const fullName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
    if (!fullName) {
        return null;
    }
    const AF = addressFields;
    return (
        <Typography {...styles.customerNameText}>
            {firstName && (AF?.firstName?.isVisible || !AF) && (
                <Typography {...styles.firstNameText} data-test-selector="addressInfoDisplay_firstName">
                    {`${firstName} `}
                </Typography>
            )}
            {lastName && (AF?.lastName?.isVisible || !AF) && (
                <Typography {...styles.lastNameText} data-test-selector="addressInfoDisplay_lastName">
                    {lastName}
                </Typography>
            )}
        </Typography>
    );
};

interface CityStatePostalCodeProps {
    city: string;
    state?: string;
    postalCode: string;
    styles: AddressInfoDisplayStyles;
    addressFields?: AddressFieldDisplayCollectionModel;
}

export const CityStatePostalCode = ({ city, state, postalCode, styles, addressFields }: CityStatePostalCodeProps) => {
    const AF = addressFields;
    return (
        <Typography {...styles.cityStatePostalCodeText}>
            {city && (AF?.city?.isVisible || !AF) && (
                <Typography {...styles.cityText} data-test-selector="addressInfoDisplay_city">
                    {city}
                </Typography>
            )}
            {state && (
                <>
                    {", "}
                    <Typography {...styles.stateText} data-test-selector="addressInfoDisplay_state">
                        {state}
                    </Typography>
                </>
            )}
            {postalCode && (AF?.postalCode?.isVisible || !AF) && (
                <>
                    {", "}
                    <Typography {...styles.postalCodeText} data-test-selector="addressInfoDisplay_postalCode">
                        {postalCode}
                    </Typography>
                </>
            )}
        </Typography>
    );
};

export default AddressInfoDisplay;
